<template>
  <Card
    animate-hover
    desktop-with-border
    mobile-with-border
  >
    <div class="simple-get-away-by-hotel">
      <div class="simple-get-away-by-hotel__images">
        <div class="simple-get-away-by-hotel__images__wrapper">
          <GalleryMini
            :uuid="getAwayGroupByHotel.hotel.uuid"
            :images="images"
            :display-controls="false"
          />
          <div class="simple-get-away-by-hotel__images__wrapper__tags" />
        </div>
      </div>

      <div class="simple-get-away-by-hotel__name-location">
        <div class="simple-get-away-by-hotel__name-location__hotel">
          <HotelNameStarsAtom
            standalone
            :hotel="getAwayGroupByHotel.hotel"
          />
        </div>
        <div class="simple-get-away-by-hotel__name-location__spa">
          {{ spaName }}
        </div>

        <div class="simple-get-away-by-hotel__name-location__location">
          <LocationLink
            :location="location"
            :show-icon="true"
            :go-to-map-enabled="false"
          />
        </div>

        <div class="simple-get-away-by-hotel__name-location__dates">
          <template v-if="hasAnyAvailability && showAvailabilityDates">
            <p class="simple-get-away-by-hotel__name-location__dates__label">
              <CalendarIcon /> <Lang by-key="get-away-price-for" />:
            </p>
            <p>{{ availabilityDateFromStr }}, {{ availabilityDateToStr }}</p>
          </template>

          <template v-if="!hasAnyAvailability && hasValidSearch && showAvailabilityDates && availabilityDateFromStr && availabilityDateToStr">
            <p class="simple-get-away-by-hotel__name-location__dates__label">
              <Lang by-key="get-away-no-availability-for-dates" />:
            </p>
            <p>{{ availabilityDateFromStr }}, {{ availabilityDateToStr }}</p>
          </template>
        </div>

        <div class="simple-get-away-by-hotel__name-location__pax">
          <template v-if="hasAnyAvailability">
            <PersonIcon
              variant="semi-light"
              :size="20"
            /> {{ paxLabel }}
          </template>
        </div>
      </div>

      <div class="simple-get-away-by-hotel__availability">
        <div class="simple-get-away-by-hotel__availability__title">
          <Lang
            by-key="escapada"
            plural
            capitalize-all
          />
        </div>

        <div class="simple-get-away-by-hotel__availability__items-with-avail">
          <template
            v-for="(getAway, i) in getAwaysWithAvailability"
            :key="getAway.uuid"
          >
            <div
              class="simple-get-away-by-hotel__availability__items-with-avail__item name"
              :class="{last: i === (getAwaysWithAvailability.length - 1)}"
            >
              <span>{{ getTreatmentOrGetAwayName(getAway.getAway, getAway.availability) }} </span>
            </div>

            <div
              class="simple-get-away-by-hotel__availability__items-with-avail__item refund-policy"
              :class="{last: i === (getAwaysWithAvailability.length - 1)}"
            >
              <DynLang
                v-if="isValidAvailability(getAway.availability) && !isRefundable(getAway.availability)"
                by-key="no-refundable"
                capitalize-first
              />
            </div>

            <div
              class="simple-get-away-by-hotel__availability__items-with-avail__item price"
              :class="{last: i === (getAwaysWithAvailability.length - 1)}"
            >
              <div v-if="isValidAvailability(getAway.availability)">
                <span class="only-desktop-block">desde</span>
                <span>{{ getPriceStr(getAway.availability) }}</span>
                <Tooltip
                  icon-variant="semi-light"
                  translatable-content-key="get-away-you-can-modify"
                />
              </div>
            </div>

            <div
              class="simple-get-away-by-hotel__availability__items-with-avail__item booking-btn"
              :class="{last: i === (getAwaysWithAvailability.length - 1)}"
            >
              <SplNuxtLink
                is-external
                no-lang
                :to="getLink(getAway.getAway)"
                :query="getLinkQuery(getAway.availability)"
              >
                <SplButton
                  variant="acent"
                  size="w-full"
                >
                  <span>
                    <span class="only-desktop-block">
                      Ver escapada
                    </span>
                    <span class="only-mobile">
                      Ver
                    </span>
                  </span>
                </SplButton>
              </SplNuxtLink>
            </div>
          </template>
        </div>

        <div class="simple-get-away-by-hotel__availability__items-without-avail">
          <template
            v-for="(getAway, i) in getAwaysWithoutAvailability"
            :key="getAway.uuid"
          >
            <div
              class="simple-get-away-by-hotel__availability__items-without-avail__item name"
              :class="{last: i === (getAwaysWithoutAvailability.length - 1)}"
            >
              {{ getTreatmentOrGetAwayName(getAway.getAway, null) }}
            </div>

            <div
              class="simple-get-away-by-hotel__availability__items-without-avail__item booking-btn"
              :class="{last: i === (getAwaysWithoutAvailability.length - 1)}"
            >
              <SplNuxtLink
                is-external
                no-lang
                :to="getLink(getAway.getAway)"
                :query="getLinkQuery(getAway.availability)"
              >
                <div>
                  <span class="only-desktop-block">
                    <span v-if="!hasAnyAvailability && hasValidSearch && showAvailabilityDates && availabilityDateFromStr && availabilityDateToStr">
                      <Lang
                        by-key="search-other-date"
                        capitalize-first
                      />
                    </span>
                    <span v-else>
                      <Lang
                        by-key="search-by-date"
                        capitalize-first
                      />
                    </span>
                  </span>
                  <span class="only-mobile">
                    <Lang
                      by-key="search"
                      capitalize-first
                    />
                  </span>
                </div>
              </SplNuxtLink>
            </div>
          </template>
        </div>
      </div>
    </div>
  </Card>
</template>

<script lang="ts">
import Card from '../shared/Card.vue'
import GalleryMini from '../shared/gallery/GalleryMini.vue'
import Badge from '../shared/Badge.vue'
import Tooltip from '../shared/Tooltip.vue'

import LocationLink from '../shared/LocationLink.vue'
import SplNuxtLink from '../shared/SplNuxtLink.vue'
import SplButton from '../shared/form/SplButton.vue'
import HotelNameStarsAtom from '../hotel/atom/HotelNameStars.atom.vue'

import ScoreLeafIcon from '../shared/icons/ScoreLeaf.icon.vue'
import CalendarIcon from '../shared/icons/Calendar.icon.vue'
import PersonIcon from '../shared/icons/Person.icon.vue'

import { useGetAwayAvailability } from '~/core/composable/get-away/useGetAwayAvailability'

import { dateToDDmmYYY, dateToYYYYMMDD } from '~/core/ts/util/date'
import { capitalizeFirstWord, includesInsensitive } from '~/core/ts/util/string'
import { GetAwayGroupByHotel } from '~/core/ts/entity/GetAwayGroupByHotel'
import { type GetAwayAvailability } from '~/core/ts/entity/GetAwayAvailability'
import { capitalizeAllWords } from '~/core/ts/util/string'
import { translate, translateByKey } from '~/core/ts/util/translate'

import { Money } from '~/core/ts/entity/Money'
import { GetAway } from '~/core/ts/entity/GetAway'
import { GetAwayWithAvailabilityCollection } from '~/core/ts/entity/GetAwayWithAvailability'
import { type Treatment } from '~/core/ts/entity/Treatment'

export default defineNuxtComponent({
  components: {
    Card,
    GalleryMini,
    Badge,
    Tooltip,
    LocationLink,
    SplNuxtLink,
    SplButton,
    HotelNameStarsAtom,
    ScoreLeafIcon,
    CalendarIcon,
    PersonIcon,
  },
  props: {
    getAwayGroupByHotel:{
      type: Object as PropType<GetAwayGroupByHotel>,
      required: true,
    },
    availabilityFrom: {
      type: Object as PropType<Date | null>,
        default: () => null,
    },
    availabilityTo: {
      type: Object as PropType<Date | null>,
      default: () => null,
    },
    showAvailabilityDates: {
      type: Boolean,
      default: false,
    },
    hasValidSearch: {
      type: Boolean,
      default: false,
    },
    treatments: {
      type: Array as PropType<Array<Treatment>>,
      default: () => [],
    },
    selectedTreatmentIds: {
      type: Array as PropType<Array<string>>,
      default: () => [],
    },
  },
  setup(props) {
    const {
      ensureAvailability,
      ensureAvailabilityBestRate,
      isValidAvailability
    } = useGetAwayAvailability()


    const images = computed(() => {
      if (props.getAwayGroupByHotel.getAways.length) {
        return props.getAwayGroupByHotel.getAways[0].getAway.images
      }

      if (props.getAwayGroupByHotel.spa.images.length) {
        return props.getAwayGroupByHotel.spa.images
      }

      if (props.getAwayGroupByHotel.hotel.images.length) {
        return props.getAwayGroupByHotel.hotel.images
      }

      return []
    })
    const location = props.getAwayGroupByHotel.spa.location

    const spaName = (() => {
      const originalName = props.getAwayGroupByHotel.spa.name

      const lowercase = originalName.toLowerCase()

      if (!lowercase.includes('spa')) {
        return `Spa ${originalName}`
      }

      return originalName
    })()

    const availabilityDateFromStr = computed(() => {
      if (!props.availabilityFrom) {
        return ''
      }
      return dateToDDmmYYY(props.availabilityFrom)
    })

    const availabilityDateToStr = computed(() => {
      if (!props.availabilityTo) {
        return ''
      }
      return dateToDDmmYYY(props.availabilityTo)
    })

    const hasAnyAvailability = computed(() => {
      return GetAwayGroupByHotel.hasAnyValidAvailability(props.getAwayGroupByHotel)
    })

    const treatmentsByUUIDCache: Record<string, Treatment> = {}
    props.treatments.forEach((treat) => treatmentsByUUIDCache[treat.uuid] = treat)

    function getTreatmentsParent(uuid: string): Array<Treatment> {
      const final: Record<string, Treatment> = {}

      let treatment = treatmentsByUUIDCache[uuid]
      if (!treatment) {
        return Object.values(final)
      }

      final[treatment.uuid] = treatment

      for (const parentUUID of treatment.uuidParents) {
        treatment = treatmentsByUUIDCache[parentUUID]
        if (treatment) {
          final[treatment.uuid] = treatment
        }
      }

      return Object.values(final)
    }

    function getTreatmentOrGetAwayName(getAway: GetAway, availability: GetAwayAvailability | null): string {
      if (translate(getAway.texts).shortName) {
        return translate(getAway.texts).shortName
      }

      const uuids = GetAway.getTagUUIDs(getAway)

      const labelComps: Array<string> = []

      const spaTxt = capitalizeFirstWord(translateByKey('spa'))
      const hotelTxt = capitalizeFirstWord(translateByKey('hotel'))
      const ritualTxt = capitalizeFirstWord(translateByKey('ritual'))
      const foodTxt = capitalizeFirstWord(translateByKey('food'))
      const massageTxt = capitalizeFirstWord(translateByKey('massage'))
      const treatmentTxt = capitalizeFirstWord(translateByKey('treatment'))

      for (const uuid of uuids) {
        // Circuito Spa Y Comida (f19e817d-b538-5dad-841e-447725da90e2)
        if (uuid === 'f19e817d-b538-5dad-841e-447725da90e2') {
          labelComps.push(foodTxt)
        }

        //  circuito spa y masaje relajante (a063b6fd-33cf-59f7-b8fc-1540286ecc3d)
        if (uuid === 'a063b6fd-33cf-59f7-b8fc-1540286ecc3d') {
          labelComps.push(massageTxt)
        }
      }

      for (const uuid of uuids) {
        const treatments = getTreatmentsParent(uuid)
        if (treatments.length) {
          treatments
            .map((treat) => {
              const name = treat.title
              if (includesInsensitive(name, 'hidroterapia')) {
                return spaTxt
              }

              if (includesInsensitive(name, 'circuito')) {
                return spaTxt
              }

              if (includesInsensitive(name, 'masaje')) {
                return massageTxt
              }

              if (includesInsensitive(name, 'tratamiento')) {
                return treatmentTxt
              }

              return ritualTxt
            })
            .forEach((label) => labelComps.push(label))
        }
      }

      if (!labelComps.length) {
        if (getAway.includesSpa) {
          labelComps.push(spaTxt)
        }
      }

      // Quitamos los duplicados.
      const cleaned = [...new Set(labelComps)].slice(0, 2)

      // Movemos las etiquetas de 'Spa' al principio.
      for (let i = 1; i < cleaned.length; i++) {
        const label = cleaned[i]

        if (label === spaTxt) {
          cleaned[i] = cleaned[0]
          cleaned[0] = spaTxt
          break
        }
      }

      // Hotel + ...
      cleaned.unshift(hotelTxt)

      // ... + Desayuno
      const board = getRoomAndBoardLabelFor(availability)
      if (board) {
        cleaned.push(board)
      }

      return cleaned.join(' + ')
    }

    function getRoomAndBoardLabelFor(availability: GetAwayAvailability | null) {
      if (!isValidAvailability(availability)) {
        return ''
      }

      const avail = ensureAvailability(availability)
      const bestRate = ensureAvailabilityBestRate(avail)

      const boardtype = avail.boardtypesAvailable
        .find((boardtype) => boardtype.type === bestRate.boardtype.type)

      const boardtypeName = boardtype && boardtype.type !== 'OA'
        ? boardtype.name
        : ''

      return capitalizeAllWords(boardtypeName)
    }

    function isRefundable(availability: GetAwayAvailability | null) {
      if (!isValidAvailability(availability)) {
        return ''
      }

      const avail = ensureAvailability(availability)
      const bestRate = ensureAvailabilityBestRate(avail)

      return bestRate.cancelPolicy.refundable
    }

    function getPriceStr(availability: GetAwayAvailability | null) {
      if (!isValidAvailability(availability)) {
        return ''
      }

      const avail = ensureAvailability(availability)
      const bestRate = ensureAvailabilityBestRate(avail)

      return Money.toString(bestRate.basePriceWithDiscount, 0)
    }


    const paxLabel = computed(() => {
      for (const getAway of props.getAwayGroupByHotel.getAways) {
        if (isValidAvailability(getAway.availability)) {
          const avail = ensureAvailability(getAway.availability)
          const plural = avail.numPax !== 1

          const personTxt = capitalizeFirstWord(translateByKey('person', plural))
          return `${avail.numPax} ${personTxt}`
        }
      }

      return ''
    })

    const getAwaysWithAvailability = computed(() => {
      if (!hasAnyAvailability.value) {
        return []
      }

      const collection = new GetAwayWithAvailabilityCollection(props.getAwayGroupByHotel.getAways)
      if (props.selectedTreatmentIds.length) {
        collection.filterByIncludesTagUUIDs(props.selectedTreatmentIds)
      }
      collection.sortByCheaper()
      return collection.getWithAvailability()
    })

    const getAwaysWithoutAvailability = computed(() => {
      if (!hasAnyAvailability.value || !getAwaysWithAvailability.value.length) {
        return props.getAwayGroupByHotel.getAways
      }

      return []
    })

    function getLink(getAway: GetAway) {
      return getAway.uri
    }

    function getLinkQuery(avail: GetAwayAvailability | null) {
      if (!props.availabilityFrom) {
        return {}
      }

      if (!props.availabilityTo) {
        return {}
      }

      let pax = 2
      if (isValidAvailability(avail)) {
        pax = ensureAvailability(avail).numPax
      }

      return {
        'date-range': dateToYYYYMMDD(props.availabilityFrom) + ',' + dateToYYYYMMDD(props.availabilityTo),
        pax,
      }
    }

    return {
      images,
      spaName,
      location,
      paxLabel,

      getAwaysWithAvailability,
      getAwaysWithoutAvailability,

      hasAnyAvailability,
      isValidAvailability,

      isRefundable,
      getRoomAndBoardLabelFor,
      getPriceStr,
      getTreatmentOrGetAwayName,

      availabilityDateFromStr,
      availabilityDateToStr,

      getLink,
      getLinkQuery,
    }
  }
})
</script>

<style lang="scss" scoped>
.simple-get-away-by-hotel {
  @apply p-1;
  @apply grid;
  grid-template-columns: 90px 1fr;
  @apply gap-2;

  @screen md {
    grid-template-columns: 200px 1fr;
  }

  @screen lg {
    grid-template-columns: 300px 1fr;
  }

  &__images {
    &__wrapper {
      @apply relative;
      @apply h-full;

      &__tags {
        @apply absolute;

        top: 20px;
        right: 20px;
        @screen lg {
          top: 24px;
          right: 24px;
        }
      }
    }
  }

  &__name-location {
    @apply py-0;

    @screen md {
      @apply pl-4;
      @apply py-2;
    }

    &__spa {
      @apply text-spl-dark;
      @apply text-lg;
    }

    &__location {
      @apply py-2;
    }

    &__dates {
      @apply pt-2;
      @apply pb-1;

      @apply flex;
      @apply flex-col;
      @apply gap-0;

      @apply min-h-8;

      @screen md {
        @apply flex-row;
        @apply gap-2;
      }

      &__label {
        @apply flex;
        @apply items-center;

        @apply gap-2;
      }
    }

    &__pax {
      @apply pt-1;
      @apply pb-2;

      @apply flex;
      @apply items-center;

      @apply gap-2;
    }
  }

  &__availability {
    @apply mx-2;

    @apply border-t-1;

    grid-column: 1 / span 2;

    &__title {
      @apply font-semibold;

      @apply pb-2;
    }

    @screen md {
      @apply mx-4;

      @apply border-0;
    }

    &__items-with-avail {
      @apply grid;
      grid-template-columns: 1.1fr 1fr 1fr;

      @screen md {
        @apply mb-2;
        grid-template-columns: 2.1fr 0.8fr .9fr 1fr;
      }

      &__item {
        @apply border-b-1;
        @apply py-1;

        @apply flex;
        @apply justify-start;
        @apply items-center;
      }

      .refund-policy,
      .price,
      .booking-btn {
        @apply pb-2;
        @screen md {
          @apply pb-1;
        }
      }

      .name {
        @apply text-spl-dark;

        grid-column: 1 / span 3;
        @apply border-0;
        @screen md {
          grid-column: auto;
          @apply border-b-1;
        }

        @apply w-full;
        @apply max-w-full;
      }

      .refund-policy {
        @apply text-spl-gray-4;
        @apply justify-end;
        @apply text-center;
        // @screen md
      }

      .price {
        @apply text-spl-dark;

        @apply font-bold;
        @apply text-nowrap;

        @apply text-xl;

        @screen md {
          @apply text-lg;
        }

        @apply justify-end;
        @apply px-2;

        div {
          @apply flex;
          @apply items-center;
          @apply justify-end;
          @apply gap-2;
        }
      }

      .booking-btn {
        @apply text-xl;
        @apply underline;

        @apply justify-end;

        @apply text-spl-acent-dark;
        @apply font-semibold;
      }

      &__item.last {
        @apply border-b-0;
      }
    }

    &__items-without-avail {
      @apply text-spl-dark;

      @apply grid;
      @apply grid-cols-2;
      grid-template-columns: 40% 60%;

      &__item {
        @apply py-2;
        @apply border-b-1;

        @apply flex;
        @apply justify-start;
        @apply items-center;
      }

      &__item.last {
        @apply border-b-0;
      }

      .name {
        @apply w-full;
        @apply max-w-full;

        overflow: hidden;
        @apply overflow-ellipsis;
        @apply whitespace-nowrap;
      }

      .booking-btn {
        @apply text-xl;
        @apply underline;

        @apply justify-end;

        @apply text-spl-acent-dark;
        @apply font-semibold;
      }
    }
  }
}
</style>
